import { useLocation, useParams } from "react-router-dom";
import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import Input from "../../../components/Atomos/Inputs/Input";
import ButtonsCrud from "../../../components/Catalogs/ButtonsCrud";
import "../../../assets/css/AddCatalogs.css";
import CatalogSubTitle from "../../../components/Moleculas/Catalog Text/CatalogSubTitle";
import SaveOverride from "../../../components/Catalogs/SaveOverride";
import { useDispatch } from "react-redux";
import { AddCompany, DeleteCompany, UpdateCompany } from "../../../redux/catalogs/states";
import { ICompany } from "../../../interfaces/models/ICompany";
import InputImage from "../../../components/Atomos/InputImage/InputImage";
import { uploadImage } from "../../../utils/UploadImage";
import { createCompany, deleteCompany, updateCompany } from "../../../Services/Company";

interface iCatalogProps {
  mode: "add" | "edit";
}

type error = { error: boolean; msj: string };
type errors = {
  name: error;
  logo?: error;
};

const AddCompanyCatalog: React.FC<iCatalogProps> = (props) => {
  const crudNavegate = () => {
    navigate(-1);
  };

  const { state, pathname } = useLocation();
  const navigate = useNavigate();
  const { id } = useParams();
  const [company, setCompany] = useState<ICompany>(
    state
      ? { ...state, squareLogo: state.squareLogo ?? false }
      : { name: "", logo: "", squareLogo: false }
  );
  const [logo, setLogo] = useState<File | string | null>(
    state?.logo || null
  );
  const [buttonClicked, setButtonClicked] = useState("");
  const [errors, setErrors] = useState<errors>({
    name: { error: false, msj: "" },
  });
  const [errorFields, setErrorFields] = useState<string[]>([]);
  const [saveOverrideOpen, setSaveOverrideOpen] = useState(false);

  const dispatch = useDispatch();


  useEffect(() => {
    if (errors.name.error) {
      validateForm();
    }
  }, [company, logo]);

  useEffect(() => {
    if (!errors.name.error && !errors.logo?.error) {
      setSaveOverrideOpen(false);
    }
  }, [errorFields]);


  const validateForm = () => {
    let valid = true;
    let newerrors: errors = errors;
    newerrors.name =
      company?.name === ""
        ? { error: true, msj: "Name is required" }
        : { error: false, msj: "" };

    valid = Object.values(newerrors).every((error) => !error?.error);

    setErrors(newerrors);
    setErrorFields(
      Object.keys(newerrors).filter(
        (key) => newerrors[key as keyof typeof newerrors]?.error,
      )
    );

    if (!valid) {
      setSaveOverrideOpen(true);
    }

    return valid;
  };

  const handleButtonClick = (action: string) => {
    setButtonClicked(action);
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    switch (buttonClicked) {
      case "delete":
        await handleDelete()
        break;
      case "saveOverride":
        if (props.mode === "add") {
          await handleCreate()
        } else {
          await handleUpdate()
        }
        break;
      case "saveButton":
        await handleCreate()
        break;
      case "updateButton":
        if (validateForm()) {
          await handleUpdate()
        }
        break;
      default:
        // Handle unexpected buttonClicked values if needed
        break;
    }
  };


  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    setCompany({ ...company, [e.target.name]: e.target.value });
  };

  const handleLogoChange = useCallback((photo: string | File | null) => {
    setLogo(photo);
  }, []);

  const handleImageUpload = useCallback(async (
    image: File | string | null | undefined,
    companyId: string
  ): Promise<string | undefined> => {
    if (image instanceof File) {
      const url = await uploadImage(image, companyId, "company/logos/");
      return url;
    }
    return typeof image === "string" ? image : undefined;
  }, []);
  
  const generateCompanyData = () => ({
    ...company,
  });

  const handleCreate = async () => {
    if (!validateForm()) {
      return;
    }

    try {
      const companyId = company.id || "new";
      const logoUrl = await handleImageUpload(logo, companyId);

      if (logo instanceof File && !logoUrl) {
        setErrors((prev) => ({
          ...prev,
          logo: { error: true, msj: "Failed to upload logo" },
        }));
        return;
      }

      const companyData = {
        ...generateCompanyData(),
        logo: logoUrl ?? "",
      };
      const response = await createCompany(companyData);
      dispatch(AddCompany(response));
      crudNavegate();
    } catch (error) {
      console.error('Error creating company:', error);
    }
  };

  const handleUpdate = async () => {
    try {
      const companyId = company.id || "new";
      const logoUrl = await handleImageUpload(logo, companyId);
      
      const companyData = {
        ...generateCompanyData(),
        ...(logoUrl && { logo: logoUrl })
      };
      await updateCompany(companyData);
      dispatch(UpdateCompany(companyData));
      crudNavegate();
    } catch (error) {
      console.error('Error updating company:', error);
    }
  };

  const handleDelete = async () => {
    try {
      const companyData = generateCompanyData();
      await deleteCompany(companyData.id ?? "");
      dispatch(DeleteCompany(companyData.id ?? ""));
      crudNavegate();
    } catch (error) {
      console.error('Error deleting company:', error);
    }
  };

  return (
    <main className={`${saveOverrideOpen ? "pt-7 md:pt-9 " : ""}`}>
      <form onSubmit={handleSubmit}>
        <SaveOverride
          fields={errorFields}
          handleButtonClick={handleButtonClick}
          open={saveOverrideOpen}
          close={() => {
            setSaveOverrideOpen(!saveOverrideOpen);
          }}
        />
        <article className="articleSection ">
          <section className="formsectionNTitle">
            <CatalogSubTitle
              title=" Company Information"
              className="text-accent-Default-dark"
            />
            <div className="formsection flex w-full flex-col lg:flex-row">
              <div className=" w-full lg:w-1/2">
                <Input
                  name="name"
                  value={company.name}
                  required={true}
                  title="Name"
                  error={errors.name.error}
                  errorMsj={errors.name.msj}
                  onChange={handleInputChange}
                />
              </div>
              <div className="w-full lg:w-1/2">
                <div className="flex flex-col gap-2">
                  <InputImage
                    title="Logo"
                    setImage={handleLogoChange}
                    photo={typeof logo === "string" ? logo : ""}
                    error={errors.logo?.error ?? false}
                    errorMsg={errors.logo?.msj ?? ""}
                    dynamicSize={true}
                    maxsize={25}
                    maxPreviewWidth={240}
                    maxPreviewHeight={120}
                  />
                </div>
              </div>
            </div>
          </section>
          <ButtonsCrud
            Catalog="Company"
            mode={props.mode}
            id={id ? id : ""}
            disabled={errors.name.error || !!errors.logo?.error}
            actionButton={handleButtonClick}
            onDelete={handleDelete}
            state={state}
            newState={company}
          />
        </article>
      </form>
    </main>
  );
};
export default AddCompanyCatalog;
